import { z } from "zod";

/**
 * Booking form schemas (docs/api-design.md §19–20, Phase 4 — Availability
 * and Booking, ADR-0008). These only improve client-side UX — the backend
 * (`CreateBookingDto`, `BookingStateMachine`, ...) always re-validates.
 */

export const fulfillmentTypeSchema = z.enum(["PICKUP", "DELIVERY"]);

export const createBookingItemSchema = z.object({
  productId: z.string().uuid(),
  variantId: z.string().uuid(),
  quantity: z.number().int().min(1).max(1000),
});

export const createBookingSchema = z
  .object({
    storeId: z.string().uuid(),
    rentalStartDate: z.string().min(1),
    rentalEndDate: z.string().min(1),
    fulfillmentType: fulfillmentTypeSchema.optional(),
    deliveryAddressId: z.string().uuid().optional(),
    customerNote: z.string().trim().max(1000).optional(),
    items: z.array(createBookingItemSchema).min(1),
  })
  .superRefine((booking, ctx) => {
    if (booking.fulfillmentType === "DELIVERY" && !booking.deliveryAddressId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "deliveryAddressId is required when fulfillmentType is DELIVERY.",
        path: ["deliveryAddressId"],
      });
    }
  });
export type CreateBookingInput = z.infer<typeof createBookingSchema>;

export const confirmBookingSchema = z.object({
  storeNote: z.string().trim().max(1000).optional(),
  paymentInstruction: z.string().trim().max(2000).optional(),
});
export type ConfirmBookingInput = z.infer<typeof confirmBookingSchema>;

export const rejectBookingReasonCodeSchema = z.enum([
  "ITEM_NOT_AVAILABLE",
  "DATE_NOT_AVAILABLE",
  "QUANTITY_NOT_AVAILABLE",
  "CUSTOMER_REQUIREMENT_UNSUPPORTED",
  "STORE_UNAVAILABLE",
  "INVALID_REQUEST",
  "OTHER",
]);

export const rejectBookingSchema = z.object({
  reasonCode: rejectBookingReasonCodeSchema,
  reasonText: z.string().trim().max(1000).optional(),
});
export type RejectBookingInput = z.infer<typeof rejectBookingSchema>;

export const reportPaymentSchema = z.object({
  note: z.string().trim().max(1000).optional(),
  evidenceFileKeys: z.array(z.string().trim().min(1)).max(10).optional(),
});
export type ReportPaymentInput = z.infer<typeof reportPaymentSchema>;

export const confirmPaymentSchema = z.object({
  note: z.string().trim().max(1000).optional(),
});
export type ConfirmPaymentInput = z.infer<typeof confirmPaymentSchema>;

export const startRentalSchema = z.object({
  note: z.string().trim().max(1000).optional(),
});
export type StartRentalInput = z.infer<typeof startRentalSchema>;

export const reportReturnSchema = z.object({
  returnMethod: z.string().trim().max(100).optional(),
  trackingNumber: z.string().trim().max(100).optional(),
  note: z.string().trim().max(1000).optional(),
  evidenceFileKeys: z.array(z.string().trim().min(1)).max(10).optional(),
});
export type ReportReturnInput = z.infer<typeof reportReturnSchema>;

export const confirmReturnSchema = z.object({
  conditionNote: z.string().trim().max(1000).optional(),
  evidenceFileKeys: z.array(z.string().trim().min(1)).max(10).optional(),
});
export type ConfirmReturnInput = z.infer<typeof confirmReturnSchema>;

export const completeBookingSchema = z.object({
  storeResolutionNote: z.string().trim().max(1000).optional(),
});
export type CompleteBookingInput = z.infer<typeof completeBookingSchema>;

export const cancelBookingReasonCodeSchema = z.enum([
  "CUSTOMER_CHANGED_MIND",
  "CUSTOMER_PAYMENT_NOT_COMPLETED",
  "STORE_CANNOT_FULFILL",
  "ITEM_DAMAGED_BEFORE_RENTAL",
  "ITEM_LOST",
  "SHIPPING_FAILURE",
  "AGREEMENT_BETWEEN_PARTIES",
  "ADMIN_ACTION",
  "FRAUD_SUSPECTED",
  "OTHER",
]);

export const cancelBookingSchema = z.object({
  reasonCode: cancelBookingReasonCodeSchema,
  reasonText: z.string().trim().max(1000).optional(),
});
export type CancelBookingInput = z.infer<typeof cancelBookingSchema>;

/**
 * Store-wide block — no `productId`/`variantId`; the seller just marks the
 * date range as closed ("วันหยุด") or under maintenance ("ปรับปรุง"). See
 * `CreateCalendarBlockRequest` in @chaochud/contracts.
 */
export const createCalendarBlockSchema = z.object({
  startDate: z.string().min(1),
  endDate: z.string().min(1),
  type: z.enum(["STORE_BLOCK", "MAINTENANCE"]),
  reason: z.string().trim().max(500).optional(),
});
export type CreateCalendarBlockInput = z.infer<typeof createCalendarBlockSchema>;

// ---------------------------------------------------------------------------
// Walk-in / in-store checkout (walk-in checkout addition)
// ---------------------------------------------------------------------------

const phoneNumberPattern = /^[0-9+\-\s]{6,30}$/;

export const paymentMethodSchema = z.enum(["CASH", "CARD", "BANK_TRANSFER", "OTHER"]);

export const walkInCustomerSchema = z
  .object({
    existingUserId: z.string().uuid().optional(),
    phoneNumber: z.string().trim().regex(phoneNumberPattern, "phoneNumber is invalid").optional(),
    displayName: z.string().trim().min(1).max(200).optional(),
    firstName: z.string().trim().max(100).optional(),
    lastName: z.string().trim().max(100).optional(),
  })
  .superRefine((customer, ctx) => {
    if (customer.existingUserId) {
      return;
    }
    if (!customer.phoneNumber) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "phoneNumber is required.", path: ["phoneNumber"] });
    }
    if (!customer.displayName) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "displayName is required.", path: ["displayName"] });
    }
  });
export type WalkInCustomerInput = z.infer<typeof walkInCustomerSchema>;

export const createWalkInBookingSchema = z.object({
  rentalStartDate: z.string().min(1),
  rentalEndDate: z.string().min(1),
  customerNote: z.string().trim().max(1000).optional(),
  items: z.array(createBookingItemSchema).min(1),
  customer: walkInCustomerSchema,
  paymentMethod: paymentMethodSchema,
  paymentNote: z.string().trim().max(2000).optional(),
});
export type CreateWalkInBookingInput = z.infer<typeof createWalkInBookingSchema>;

export const lookupCustomerByPhoneSchema = z.object({
  phoneNumber: z.string().trim().regex(phoneNumberPattern, "phoneNumber is invalid"),
});
export type LookupCustomerByPhoneInput = z.infer<typeof lookupCustomerByPhoneSchema>;
